import React from "react"
import PropTypes from "prop-types"
import Textarea from 'react-textarea-autosize';
import populateVoices from '../populateVoices';
import store from '../store';
import defaultGameSettings from '../defaultGameSettings';
import { AUDIO_REPLAY_DELAY, WORD_COUNT_DELAY } from '../timing';
import Modal from './Modal';
import PhraseEditor from './PhraseEditor';
import getSelectionText from '../getSelectionText';
import { compare, normalize, sameText } from '../diff';
import { SCROLL_DURATION } from '../timing';
import { speak } from '../speak';
const rtlDetect = require('rtl-detect');

const LANGUAGE_ACCENT_MAP = {
  es: {'A':'á', 'E':'é', 'I':'í', 'O':'ó', 'U':'úü',                    // Spanish
      'N':'ñ', '!':'¡', '?':'¿'},
  fr: {'A':'àâæ', 'E':'èéêë', 'I':'îï', 'O':'ôœ', 'U':'ùûü', 'C':'ç'},  // French
  pt: {'A':'ãáâà', 'E':'éê', 'I':'í', 'O':'õóô', 'U':'úü', 'C':'ç'},    // Portuguese
  de: {'A':'ä', 'O':'ö', 'U':'ü', 'S':'ß'},                             // German
  it: {'A':'àá', 'E':'èé', 'I':'ìí', 'O':'òó', 'U':'ùú'},               // Italian
  pl: {'A':'ą', 'C':'ć', 'E':'ę', 'L':'ł', 'N':'ń',                     // Polish
      'O':'ó', 'S':'ś', 'X':'ź', 'Z':'żź'},
  ro: {'A':'ăâ', 'I':'î', 'S':'şș', 'T':'ţț'},                          // Romanian
  hu: {'A':'á', 'E':'é', 'I':'í', 'O':'öóő', 'U':'üúű'},                // Hungarian
  nl: {'A':'á', 'E':'éèë', 'I':'ï', 'O':'óö', 'U':'ü'},                 // Dutch (Netherlands)
  tr: {'C':'ç', 'G':'ğ', 'I':'ıİ', 'O':'ö', 'S':'ş', 'U':'ü'},          // Turkish
  da: {'A':'åæ', 'O':'ø'},                                              // Danish
  ga: {'A':'á', 'E':'é', 'I':'í', 'O':'ó', 'U':'ú'},                    // Irish
  sv: {'A':'åä', 'O':'ö', 'E':'é'},                                     // Swedish
  eo: {'C':'ĉ', 'G':'ĝ', 'H':'ĥ', 'J':'ĵ', 'S':'ŝ', 'U':'ŭ'},           // Esperanto
  nb: {'A':'åæ', 'O':'ø', 'E':'é'}, // Norwegian
  et: {'A':'ä', 'O':'öõ', 'U':'ü'}, // Estonian
  sk: {'A':'áä', 'C':'č', 'D':'ď', 'E':'é', 'I':'í', 'L':'ľĺ', 'N':'ň', 'O':'óô', 'R':'ŕ', 'S':'š', 'T':'ť', 'U':'ú', 'Y':'ý', 'Z':'ž'}, // Slovak
  is: {'A':'áæ', 'D':'ðþ', 'E':'é', 'I':'í', 'O':'óö', 'U':'ú', 'Y':'ý'}, // Icelandic
};

class TranslationEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      translation: props.translation
    };
  }

  save() {
    this.props.onSave(this.state.translation);
  }

  render() {
    return (
      <>
        <Textarea className="form-control mb-2" autoFocus={true} disabled={this.props.updating} value={this.state.translation || ''} onChange={(e) => this.setState({ translation: e.target.value })}></Textarea>
        <div className="text-right">
          <button disabled={this.props.updating} onClick={() => this.save()} className="btn btn-sm btn-primary">Save</button>
          <button disabled={this.props.updating} onClick={() => this.props.onCancel()} className="btn btn-sm btn-link ml-2">Cancel</button>
        </div>
      </>
    );
  }
}

class NoteEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      note: props.note
    };
  }

  save() {
    this.props.onSave(this.state.note);
  }

  render() {
    const { demo, updating } = this.props;

    const textarea = (
      <Textarea
        className="note-textarea form-control my-2"
        autoFocus={true}
        readOnly={!!demo}
        disabled={updating}
        value={this.state.note || ''}
        onChange={(e) => this.setState({ note: e.target.value })}
      ></Textarea>
    );

    if(demo) {
      let message = '';
      if(demo === 'basic') {
        message = 'Sign up or sign in to save and edit notes!';
      }
      else if(demo === 'shared') {
        message = 'Import this challenge to save and edit notes!';
      }

      return (
        <>
          {textarea}
          <div>
            <small>{message}</small>
            <button onClick={() => this.props.onCancel()} className="btn btn-sm btn-link ml-2">Close</button>
          </div>
        </>
      );
    }

    return (
      <>
        {textarea}
        <div>
          <button disabled={this.props.updating} onClick={() => this.save()} className="btn btn-sm btn-primary">Save</button>
          <button disabled={this.props.updating} onClick={() => this.props.onCancel()} className="btn btn-sm btn-link ml-2">Cancel</button>
          <small className="ml-2">
            Enter any notes you'd like to take here. You can view all your notes via your dashboard.
          </small>
        </div>
      </>
    );
  }
}

class Completions extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showAll: false
    };
  }

  render() {
    let { completions, getTimeStr } = this.props;

    if(!completions.length) {
      return null;
    }

    const { showAll } = this.state;

    completions = completions.slice(0).reverse();

    if(!showAll) {
      completions = [completions[0]];
    }

    return (
      <div>
        {completions.map((c, i) => (
          <div key={c.date}>
            <small>{c.date} Typos: {c.typos} Hints: {c.hints} Pauses: {c.pauses} Time: {getTimeStr(c.time)}</small>
            {i === 0 && this.props.completions.length > 1 && <button className="btn btn-link btn-sm" onClick={() => this.setState({ showAll: !showAll })}>{showAll ? 'Less' : 'More'}</button>}
          </div>
        ))}
      </div>
    );
  }
}


export default class Sentence extends React.Component {
  static defaultProps = {
    gameSettings: defaultGameSettings
  }

  // Languages that don't separate words with spaces. There's no "next word" to
  // reveal in them — a whitespace-based hint would hand over the entire sentence
  // — so the Word Hint button is hidden. Character hints still work.
  static SPACELESS_LANGUAGE_CODES = ['zh', 'ja']

  constructor(props) {
    super(props);

    this.inputRef = React.createRef();
    this.wordCountRef = React.createRef();
    this.id = 'sentence-' + (props.sentenceId ? props.sentenceId : new Date().getTime());
    this.isRtlLang = this.props.twoLetterLanguageCode && rtlDetect.isRtlLang(this.props.twoLetterLanguageCode);

    this.state = {
      complete: !!props.complete,
      completions: props.completions || [],
      correctSoFar: null,
      editingTranslation: false,
      elapsedTime: 0,
      favorited: props.favorited,
      highlightedPhrase: null,
      hintCount: 0,
      inReviewQueue: props.inReviewQueue || false,
      nextReview: props.nextReview || null,
      input: props.complete ? props.text : '',
      note: props.note,
      noteVisible: false,
      pauseCount: 0,
      phrases: props.phrases || [],
      playing: props.playing || false,
      showOverLimitModal: false,
      // The attempt shown in the comparison panel. Held separately from `input`
      // so editing after a submit doesn't silently rewrite the thing you're
      // being shown — the panel is a record of what you submitted, not a live view.
      submittedInput: null,
      submitCount: 0,
      reviewSuggestionDismissed: false,
      aiLoading: null,
      aiResult: null,
      aiError: null,
      aiVisible: false,
      translation: props.translation,
      translationAccepted: props.translationAccepted || false,
      typoCount: 0,
      updating: false,
      voices: store.getState().voices,
    };
  }

  onInputChange(event) {
    const value = event.target.value;
    this.setInput(value);
  }

  setInput(value) {
    return new Promise((resolve, reject) => {
      // normalize() collapses whitespace globally and trims. The /g was the fix
      // for double spaces making a sentence uncompletable; the trim covers the
      // same class of bug at the ends, where a single trailing space meant a
      // perfectly typed sentence never registered as finished.
      const caseSensitive = this.caseSensitive;
      const basicValue = caseSensitive ? normalize(value) : normalize(value).toLowerCase();
      const basicText = caseSensitive ? normalize(this.props.text) : normalize(this.props.text).toLowerCase();
      const complete = sameText(value, this.props.text, { caseSensitive });
      // using #!# so we match from the start of the text
      const correctSoFar = (('#!#' + basicText).indexOf('#!#' + basicValue) >= 0);
      // if(!correctSoFar && !this.state.compositioning && this.props.gameSettings.soundEffects) {
      //   new Audio('/audio/incorrect.mp3').play();
      // }
      this.setState({
        input: value,
        complete,
        correctSoFar: this.compositioning ? this.state.correctSoFar : correctSoFar,
        wordCountVisible: complete,
        playing: !complete
      }, () => {
        if(complete) {
          this.onComplete();
        }
        resolve(complete);
      });
    });
  }

  // Absent from game_settings rows saved before the setting existed, which
  // should read as on so nothing changes for existing players.
  get colorHints() {
    return this.props.gameSettings.colorHints !== false;
  }

  // On by default — it's the existing behaviour, and for German nouns or
  // sentence-initial capitals it's part of writing the language rather than a
  // typo. Off makes case differences count as correct everywhere: completion,
  // the live hint, the hint buttons and the comparison panel.
  get caseSensitive() {
    return this.props.gameSettings.caseSensitive !== false;
  }

  submit() {
    if(this.state.complete || !this.state.playing || !this.state.input.trim()) {
      return;
    }
    if(sameText(this.state.input, this.props.text, { caseSensitive: this.caseSensitive })) {
      // Defensive. setInput already completes on an exact match, so this is
      // unreachable in normal play — cheaper to handle than to no-op mysteriously.
      this.setInput(this.state.input);
      return;
    }
    // The answer differs. Record the attempt for the comparison panel and leave
    // the input editable so it can be corrected and submitted again.
    this.setState({
      submittedInput: this.state.input,
      submitCount: this.state.submitCount + 1
    }, () => {
      // Clicking the button moves focus to the button, which is the wrong place
      // in a flow whose whole point is "now edit your answer". Enter-to-submit
      // never leaves the textarea, so this only matters for the click path —
      // but it's the same fix either way.
      this.inputRef && this.inputRef.focus();
    });
  }

  onComplete() {
    if(this.props.sentenceId && !this.props.demo) {
      const { elapsedTime, hintCount, pauseCount, submitCount, typoCount } = this.state;
      $.ajax({
        headers: { 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') },
        url: '/progress',
        method: 'post',
        data: {
          hint_count: hintCount,
          elapsed_time: elapsedTime,
          pause_count: pauseCount,
          sentence_id: this.props.sentenceId,
          // Rejected submissions. Recorded because typo_count is derived from
          // live colour-hint transitions, so it under-counts for anyone who
          // turns hints off — this is the signal that still works either way.
          submit_count: submitCount,
          typo_count: typoCount
        }
      }).done((data) => {
        this.props.onProgressUpdate && this.props.onProgressUpdate(data);
        this.setState({ completions: data.completions });
        if(data.overLimit) {
          this.setState({ showOverLimitModal: true });
        }
      });
    }

    setTimeout(() => {
      $(this.wordCountRef.current).fadeOut(() => this.setState({ wordCountVisible: false }));
    }, WORD_COUNT_DELAY);

    if(this.props.gameSettings.soundEffects && this.props.gameSettings.autoPlayTts) {
      new Audio('/audio/correct.mp3').play();
      setTimeout(() => this.playAudio(false), AUDIO_REPLAY_DELAY);
    }
    else if(this.props.gameSettings.soundEffects) {
      new Audio('/audio/correct.mp3').play();
    }
    else if(this.props.gameSettings.autoPlayTts) {
      this.playAudio(false);
    }

    this.props.onComplete && this.props.onComplete(this.props.sentenceId);
  }

  componentDidMount() {
    const gameInputSelector = '.game-input.form-control';
    $('#' + this.id)
      .on('copy paste', gameInputSelector, function(e) {
        e.preventDefault();
      })
      // https://developer.mozilla.org/en-US/docs/Mozilla/IME_handling_guide
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/compositionstart_event
      .on('compositionstart', gameInputSelector, () => {
        this.compositioning = true;
      })
      .on('compositionend', gameInputSelector, () => {
        this.compositioning = false;
      });

    this.unsubscribeVoices = store.subscribe(() => this.setState({ voices: store.getState().voices }));
    populateVoices();

    if(this.props.playing) {
      this.inputRef.focus();
      this.startElapsedTimeTimer();
    }

    $('#' + this.id).on('click', '.popover .phrase', (e) => {
      e.preventDefault();
      $('[data-toggle="popover"]').popover('hide');
      this.setState({ highlightedPhrase: $(e.target).text() });
    });
  }

  componentWillUnmount() {
    this.unsubscribeVoices && this.unsubscribeVoices();
  }

  getCurrentTimeInSeconds() {
    return Math.round(new Date().getTime() / 1000);
  }

  getCurrentDateStr() {
    let d = new Date(),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2) 
        month = '0' + month;
    if (day.length < 2) 
        day = '0' + day;

    return [year, month, day].join('-');
  }

  componentDidUpdate(prevProps, prevState) {
    if(this.state.playing && prevState.correctSoFar !== this.state.correctSoFar && !this.state.correctSoFar && !this.compositioning) {
      this.setState({ typoCount: this.state.typoCount + 1 });
    }
    if(prevState.playing !== this.state.playing) {
      if(this.state.playing) {
        this.startElapsedTimeTimer();
      }
      else {
        this.stopElapsedTimeTimer();
      }
    }
    if(prevState.playing !== this.state.playing && !this.state.playing && !this.state.complete) {
      this.setState({ pauseCount: this.state.pauseCount + 1 });
    }
    if(!prevState.complete && this.state.complete) {
      const { hintCount, pauseCount, elapsedTime, typoCount } = this.state;
      this.setState({
        completions: this.state.completions.concat([{
          date: this.getCurrentDateStr(),
          hints: hintCount,
          pauses: pauseCount,
          time: elapsedTime,
          typos: typoCount
        }])
      });
    }
  }

  startElapsedTimeTimer() {
    this.elapsedTimeTimer = setInterval(() => this.setState({ elapsedTime: Math.min(this.state.elapsedTime + 1, 300) }), 1000);
  }

  stopElapsedTimeTimer() {
    clearInterval(this.elapsedTimeTimer);
  }

  playAudio(alertIfNoVoice) {
    // android uses es_ES, we're using es-ES internally (same as mac os chrome)
    const regexp = new RegExp(this.props.voiceLocale.replace(/[-_]/, '.*'), 'i');
    const voices = this.state.voices || [];
    let voice = null;
    if(this.props.gameSettings.ttsVoice) {
      voice = voices.find((v) => v.name === this.props.gameSettings.ttsVoice);
    }
    // if no ttsVoice or still no voice after trying to find ttsVoice, attempt to find any matching
    if(!voice) {
      voice = (this.state.voices || []).find((v) => v.lang.match(regexp));
    }
    if(!voice && alertIfNoVoice !== false) {
      return alert('No voice found! Please wait a moment and try again, or try another browser. Google Chrome usually works best.');
    }
    if(!voice) {
      return false;
    }
    return speak(this.props.text, voice);
  }

  renderPlayPauseResetBtn() {
    if(this.state.complete) {
      return <button ref={(el) => this.playPauseBtnRef = el} className="btn btn-sm btn-light" onClick={this.onResetClick.bind(this)}>Reset</button>;
    }
    return <button ref={(el) => this.playPauseBtnRef = el} className={'btn btn-sm btn-' + (this.state.playing ? 'light' : 'primary')} onClick={this.onPlayPauseClick.bind(this)}>{this.state.playing ? 'Pause' : 'Play'}</button>;
  }

  // How much of the input matches the target from the start. Hints are
  // position-based — they reveal the target character at your cursor — so they
  // only make sense measured from a point where the two still agree.
  //
  // Hinting from here also repairs: anything typed after the divergence is
  // dropped, which is what you'd do by hand anyway. Previously the buttons
  // simply disappeared once you went wrong, which took the help away at exactly
  // the moment it was wanted — and now that you can type straight past a
  // mistake, that state is easy to sit in indefinitely.
  correctPrefixLength() {
    const fold = (c) => (this.caseSensitive ? c : c.toLowerCase());
    const input = this.state.input;
    const text = this.props.text;
    let i = 0;
    while(i < input.length && i < text.length && fold(input[i]) === fold(text[i])) {
      i++;
    }
    return i;
  }

  // Called by the parent when the sentence above this one is finished. Focuses
  // whichever button actually moves the player forward, so the next sentence can
  // be started from the keyboard without hunting for it.
  focusPrimaryAction() {
    const button = this.state.translationAccepted ? this.playPauseBtnRef : this.acceptBtnRef;
    if(!button) {
      return;
    }
    this.scrollIntoViewNicely(() => button.focus());
  }

  // Same idea as focusPrimaryAction, but performs it — for the Enter hotkey,
  // where the button doesn't have focus to press.
  //
  // Deliberately state-dependent rather than "click the primary button":
  // mid-sentence that button reads Pause, and Enter stopping the clock would be
  // the opposite of moving forward.
  triggerPrimaryAction() {
    if(this.state.complete) {
      return;
    }

    if(!this.state.translationAccepted) {
      this.acceptBtnRef && this.scrollIntoViewNicely(() => this.acceptBtnRef.click());
      return;
    }

    if(!this.state.playing) {
      this.playPauseBtnRef && this.scrollIntoViewNicely(() => this.playPauseBtnRef.click());
      return;
    }

    // Already under way — put the cursor back where the typing goes.
    this.scrollIntoViewNicely(() => this.inputRef && this.inputRef.focus());
  }

  // Aligns the top of the card just under the banner. Deliberately top-aligned
  // rather than centred: a sentence taller than the viewport has to start at the
  // top to be usable at all, and using one rule for both cases means the panel
  // always lands in the same place.
  scrollIntoViewNicely(callback) {
    const el = this.ref;
    if(!el) {
      return callback && callback();
    }

    const BANNER_HEIGHT = 36;
    const rect = el.getBoundingClientRect();
    const fullyVisible = rect.top >= BANNER_HEIGHT && rect.bottom <= window.innerHeight;

    if(fullyVisible) {
      return callback && callback();
    }

    const target = Math.max(0, window.scrollY + rect.top - BANNER_HEIGHT - 12);
    $('html, body').animate({ scrollTop: target }, SCROLL_DURATION, () => callback && callback());
  }

  giveHint() {
    const from = this.correctPrefixLength();
    this.applyHint(this.props.text.slice(from, from + 1), from);
  }

  // Reveals the rest of the current word plus the whitespace after it, so the
  // next thing you type starts a fresh word. If the input already sits on a word
  // boundary this reveals the whole of the next word.
  giveWordHint() {
    const from = this.correctPrefixLength();
    const remaining = this.props.text.slice(from);
    const match = remaining.match(/^\s*\S+\s*/);

    this.applyHint(match ? match[0] : remaining, from);
  }

  applyHint(hint, from = null) {
    if(!hint) {
      return false;
    }

    const base = from === null ? this.state.input : this.state.input.slice(0, from);

    // Counted per hint *action*, not per character revealed, so a word hint is
    // one hint the same as a character hint.
    this.setState({
      hintCount: this.state.hintCount + 1
    });

    this.setInput(base + hint).then((complete) => {
      if(!complete) {
        this.inputRef.focus();
        this.inputRef.selectionStart = this.inputRef.selectionEnd = this.state.input.length;
      }
    });
  }

  hintsAvailable() {
    return this.state.playing;
  }

  renderHintBtn() {
    if(!this.hintsAvailable()) {
      return null;
    }
    return <button className="btn btn-sm btn-light ml-2" onClick={() => this.giveHint()}>Hint</button>;
  }

  renderWordHintBtn() {
    if(!this.hintsAvailable()) {
      return null;
    }

    if(Sentence.SPACELESS_LANGUAGE_CODES.includes(this.props.twoLetterLanguageCode)) {
      return null;
    }

    return <button className="btn btn-sm btn-light ml-2" onClick={() => this.giveWordHint()}>Word Hint</button>;
  }

  onResetClick() {
    this.setState({
      complete: false,
      correctSoFar: null,
      elapsedTime: 0,
      hintCount: 0,
      input: '',
      pauseCount: 0,
      playing: false,
      submittedInput: null,
      submitCount: 0,
      reviewSuggestionDismissed: false,
      aiResult: null,
      aiError: null,
      aiVisible: false,
      typoCount: 0,
      wordCountVisible: false
    }, () => this.props.onReset && this.props.onReset(this.props.sentenceId));
  }

  play(callback) {
    this.setState({ translationAccepted: true, playing: true }, () => callback && callback());
  }

  onPlayPauseClick() {
    this.setState({ playing: !this.state.playing }, () => {
      if(this.state.playing) {
        this.inputRef.focus();
      }
    });
  }

  getScrollTop() {
    return $(this.inputRef).parents('.sentence').offset().top;
  }

  onAccentButtonPress(event, accent) {
    const x = this.inputRef.selectionStart;
    if(event.shiftKey) {
      accent = accent.toUpperCase();
    }
    this.setInput(this.state.input.substring(0, x) + accent + this.state.input.substring(x)).then((complete) => {
      if(!complete) {
        this.inputRef.focus();
        this.inputRef.selectionStart = this.inputRef.selectionEnd = x + 1;
      }
    });
  }

  renderAccentButtons() {
    if(!this.state.playing) {
      return null;
    }

    if(!LANGUAGE_ACCENT_MAP[this.props.twoLetterLanguageCode]) {
      return null;
    }

    let accents = Object.values(LANGUAGE_ACCENT_MAP[this.props.twoLetterLanguageCode]).join('').split('');
    const additionalChars = this.props.text.match(new RegExp('[^a-z.\\d\\s:",;\'?!@#$%&*()\\-=_+\\[\\]\\|/{}' + accents.join('') + ']', 'ig'));
    if(additionalChars) {
      // only uniques, https://stackoverflow.com/questions/1960473/get-all-unique-values-in-a-javascript-array-remove-duplicates
      accents = accents.concat(additionalChars.filter((value, index, self) => self.indexOf(value) === index));
    }

    let buttons = [];
    for(let i = 0, n = accents.length; i < n; i++) {
      buttons.push(<button key={accents[i]} onClick={(e) => this.onAccentButtonPress(e, accents[i])} className="btn btn-secondary btn-sm">{this.state.shiftKey ? accents[i].toUpperCase() : accents[i]}</button>);
    }

    // Hidden on mobile: those keyboards make accents easy already.
    //
    // In normal flow rather than absolutely positioned. It used to be
    // `position: absolute; left: 50%; bottom: 20`, but nothing above it was
    // positioned, so it anchored to the document rather than the card — which
    // held together only while the card's height never changed. It now shares
    // the sentence with a comparison panel, an AI panel and a review prompt, and
    // it ran straight into them.
    return (
      <div className="d-none d-sm-block text-center mt-2 mb-1">
        <div className="btn-group flex-wrap">
          {buttons}
        </div>
      </div>
    );
  }

  onInputKeyDown(event) {
    const key = event.which || event.keyCode;
    if(event.ctrlKey && key === 32) { // space bar
      this.playAudio(true);
      event.preventDefault();
      return false;
    }
    if(event.ctrlKey && key === 72) { // h
      this.giveHint();
      event.preventDefault();
      return false;
    }

    // Enter submits. Guarded against IME composition because confirming a
    // candidate in Japanese or Chinese sends Enter — without this, every kanji
    // conversion would submit the answer. isComposing is the standard signal;
    // this.compositioning is our own flag, kept as a fallback. Shift+Enter is
    // left alone as an escape hatch for anyone who wants a newline.
    const composing = this.compositioning || (event.nativeEvent && event.nativeEvent.isComposing);
    if(key === 13 && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey && !composing) {
      event.preventDefault();
      this.submit();
      return false;
    }

    this.setState({ shiftKey: event.shiftKey });
  }

  onInputKeyUp(event) {
    this.setState({ shiftKey: event.shiftKey });
  }

  deleteSentence() {
    if(confirm('Are you sure you want to delete this sentence? This action cannot be undone.')) {
      this.props.onDelete && this.props.onDelete();

      $.ajax({
        method: 'delete',
        url: '/sentences/' + this.props.sentenceId
      }).fail(() => {
        alert('Oh no! There was an error deleting that sentence. Sorry about that. Please refresh the page and try again, and let us know if you see this error message again.');
      });
    }
  }

  update(attrs) {
    if(!this.props.sentenceId || this.props.demo) {
      if(this.props.demo === 'shared') {
        this.props.demoAlert();
      }
      this.setState(attrs);
      this.props.onUpdate && this.props.onUpdate(attrs);
      return false;
    }

    this.setState({ updating: true });
    $.ajax({
      method: 'put',
      url: '/sentences/' + this.props.sentenceId,
      data: { sentence: attrs }
    })
      .done(() => {
        this.setState(attrs);
        this.props.onUpdate && this.props.onUpdate(attrs);
      })
      .fail(() => {
        alert('Oh no! There was an error updating the sentence. Sorry about that. Please refresh the page and try again, and let us know if you see this error message again.');
      })
      .always(() => {
        this.setState({ updating: false })
      });
  }

  toggleFavorite() {
    this.update({ favorited: !this.state.favorited });
  }

  // Separate endpoint rather than going through update(), because the review
  // queue isn't a sentence attribute the user edits — adding one initialises its
  // SM-2 schedule server-side.
  toggleReviewQueue() {
    if(!this.props.sentenceId || this.props.demo) {
      return false;
    }

    const inQueue = !this.state.inReviewQueue;

    this.setState({ updating: true, inReviewQueue: inQueue });

    // The verb carries the intent — no request body, so nothing to serialise and
    // nothing for the server to interpret.
    $.ajax({
      method: inQueue ? 'post' : 'delete',
      url: '/sentences/' + this.props.sentenceId +
        (inQueue ? '/add_to_review_queue' : '/remove_from_review_queue')
    })
      .done((data) => this.setState({
        updating: false,
        inReviewQueue: data.inReviewQueue,
        nextReview: data.nextReview
      }))
      .fail(() => this.setState({ updating: false, inReviewQueue: !inQueue }));
  }

  // "in 6 days" / "tomorrow" / "today", or null if we can't work it out. Used to
  // turn the button into a status so the feature explains itself: you click
  // "Review later" and it tells you when the sentence is coming back.
  reviewDueLabel() {
    const { nextReview } = this.state;

    if(!nextReview) {
      return null;
    }

    // nextReview is a plain date ('2026-08-27'), so compare local midnights to
    // avoid the answer drifting by a day across time zones.
    const due = new Date(nextReview + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = Math.round((due - today) / 86400000);

    if(!isFinite(days)) return null;
    if(days <= 0) return 'today';
    if(days === 1) return 'tomorrow';

    return 'in ' + days + ' days';
  }

  // Fragment rather than an array so React doesn't want keys. Rendered in two
  // places — inline on wide screens, inside the More dropdown on narrow ones —
  // and only one copy is ever visible, so the duplicate click handlers are inert.
  renderSecondaryBtns() {
    return (
      <>
        {this.renderExplainBtn()}
        {this.renderFavoriteBtn()}
        {this.renderReviewQueueBtn()}
        {this.renderNoteBtn()}
        {this.renderPhraseBtn()}
        {this.renderShareBtn()}
      </>
    );
  }

  renderReviewQueueBtn() {
    // Only offered once the sentence is complete. Scheduling something you
    // haven't answered yet has nothing to schedule from, and the whole point is
    // that this is opt-in per sentence after you've played it.
    if(!this.props.reviewQueueable || !this.state.complete) {
      return null;
    }

    const { inReviewQueue } = this.state;
    const due = inReviewQueue && this.reviewDueLabel();

    // Labelled rather than icon-only. A clock meaning "spaced repetition" isn't a
    // convention anyone would recognise, and a title attribute only helps people
    // who already suspect there's something to hover. The row already uses text
    // for its non-obvious actions — Hint, Word Hint, Play, Reset — so this is
    // consistent rather than an exception.
    return (
      <button
        disabled={this.state.updating}
        className={'btn btn-sm ml-2 ' + (inReviewQueue ? 'btn-secondary' : 'btn-light')}
        title={inReviewQueue ? 'Click to remove from your review queue' : 'Practise this sentence again later, on a spaced repetition schedule'}
        onClick={() => this.toggleReviewQueue()}
      >
        <i className={'fa mr-1 ' + (inReviewQueue ? 'fa-check' : 'fa-clock-o')}></i>
        {inReviewQueue ? (due ? 'Review ' + due : 'In review queue') : 'Review later'}
      </button>
    );
  }

  // The review queue is opt-in and nothing ever mentions it, which costs more
  // than low adoption: a player with an empty queue has nothing due tomorrow, so
  // the daily-goal modal has no concrete reason to offer them to come back, and
  // the daily reminder has nothing to point at. No queue, no return trigger.
  //
  // Shown only when this attempt produced evidence you'd benefit — a hint, or an
  // answer submitted and rejected. Typos are deliberately not evidence: a single
  // mistyped character is noise, and suggesting after every sentence is how a
  // prompt becomes wallpaper.
  renderReviewQueueSuggestion() {
    const { complete, inReviewQueue, hintCount, submitCount, reviewSuggestionDismissed } = this.state;

    if(!this.props.reviewQueueable || this.props.demo || !complete) {
      return null;
    }
    if(inReviewQueue || reviewSuggestionDismissed) {
      return null;
    }
    if(hintCount === 0 && submitCount === 0) {
      return null;
    }

    return (
      <div className="alert alert-info d-flex flex-wrap justify-content-between align-items-center py-2 mt-2 mb-0">
        <span className="mr-3">
          {hintCount > 0 ? 'Needed a hint on this one?' : 'Took a couple of goes?'}
          {' '}Add it to your review queue and it'll come back in a few days.
        </span>
        <span className="text-nowrap">
          <button
            disabled={this.state.updating}
            className="btn btn-sm btn-primary"
            onClick={() => this.toggleReviewQueue()}
          >
            Review later
          </button>
          <button
            className="btn btn-sm btn-link"
            onClick={() => this.setState({ reviewSuggestionDismissed: true })}
          >
            No thanks
          </button>
        </span>
      </div>
    );
  }

  // Shown once, above the first sentence of a player's first ever game.
  //
  // The same explanation already exists in articles/_help, but it's behind a
  // "Help!" collapse — which only helps people who already suspect there's
  // something they've misunderstood. Someone who thinks they understand the page
  // and doesn't will never open it.
  //
  // Disappears as soon as they accept, because by then they're doing it.
  renderFirstSentenceHelp() {
    if(!this.props.firstEver || !this.props.isNext || this.state.translationAccepted) {
      return null;
    }

    return (
      <div className="alert alert-primary py-2 mb-2">
        <strong>New here?</strong> Read the sentence and its translation below.
        Then hit <strong>Play</strong> — the sentence hides, and you type it out
        from the English.
      </div>
    );
  }

  // Both AI calls share one request path, one loading flag and one result panel.
  // They ask different questions but render the same shape, and keeping them
  // together means the disabled/loading/error handling can't drift apart.
  askAi(kind) {
    if(!this.props.sentenceId || this.props.demo || this.state.aiLoading) {
      return false;
    }

    // Already have this answer — toggle it rather than fetching it again. The
    // result is held either way, so hiding and re-showing costs nothing.
    if(this.state.aiResult && this.state.aiResult.kind === kind) {
      this.setState({ aiVisible: !this.state.aiVisible });
      return;
    }

    const url = '/sentences/' + this.props.sentenceId + '/' + (kind === 'explain' ? 'explain' : 'ask_ai');

    this.setState({ aiLoading: kind, aiResult: null, aiError: null, aiVisible: true });

    $.ajax({
      headers: { 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') },
      method: 'post',
      url: url,
      // Only ask_ai needs the attempt; explain is about the sentence alone.
      data: kind === 'explain' ? {} : { attempt: this.state.submittedInput || this.state.input }
    })
      .done((data) => this.setState({ aiResult: { kind, data } }))
      .fail((xhr) => this.setState({
        aiError: (xhr.responseJSON && xhr.responseJSON.error) || "Sorry, something went wrong."
      }))
      .always(() => this.setState({ aiLoading: null }));
  }

  aiShowing(kind) {
    return !!(this.state.aiVisible && this.state.aiResult && this.state.aiResult.kind === kind);
  }

  renderAiResult() {
    const { aiLoading, aiResult, aiError, aiVisible } = this.state;

    if(aiLoading) {
      return (
        <div className="alert alert-light py-2 mt-2 mb-3">
          <span className="spinner-border spinner-border-sm mr-2"></span>
          {aiLoading === 'explain' ? 'Looking at the sentence…' : 'Checking your answer…'}
        </div>
      );
    }

    if(aiError) {
      return <div className="alert alert-warning py-2 mt-2 mb-3">{aiError}</div>;
    }

    if(!aiResult || !aiVisible) {
      return null;
    }

    // Hide the explanation too, not just the button: opening it during the read
    // phase and then hitting Play would otherwise leave the answer on screen.
    if(aiResult.kind === 'explain') {
      if(this.state.playing) {
        return null;
      }
      return (
        <div className="alert alert-light py-2 mt-2 mb-3">
          <ul className="mb-0 pl-3">
            {(aiResult.data.points || []).map((point, i) => <li key={i}>{point}</li>)}
          </ul>
        </div>
      );
    }

    // Colour carries the verdict, but the words have to as well — "acceptable"
    // and "incorrect" are a meaningful distinction and green/amber alone won't
    // convey which one this is.
    const { verdict, note } = aiResult.data;
    const theme = verdict === 'incorrect' ? 'warning' : (verdict === 'acceptable' ? 'info' : 'success');
    const heading = {
      correct: 'That works.',
      acceptable: 'That works, with a caveat.',
      incorrect: 'Not quite.'
    }[verdict] || 'Checked.';

    return (
      <div className={'alert alert-' + theme + ' py-2 mt-2 mb-3'}>
        <strong>{heading}</strong>
        {note ? ' ' + note : ''}
      </div>
    );
  }

  onNoteBtnClick() {
    this.setState({ noteVisible: !this.state.noteVisible });
  }

  renderExplainBtn() {
    if(!this.props.aiEnabled || this.props.demo) {
      return null;
    }
    // Not while playing. The prompt tells the model to quote the fragment each
    // point is about, so an explanation is a partial answer key — the same
    // reason the sentence itself is hidden. Available before, when paused, and
    // once the sentence is done.
    if(this.state.playing) {
      return null;
    }
    return (
      <button
        className="btn btn-sm btn-light ml-2"
        disabled={!!this.state.aiLoading}
        title="What's going on grammatically in this sentence?"
        onClick={() => this.askAi('explain')}
      >
        {this.aiShowing('explain') ? 'Hide' : 'Explain'}
      </button>
    );
  }

  renderNoteBtn() {
    if(this.props.noteable) {
      return (
        <button disabled={this.state.updating} className={'btn btn-sm ml-2 note-btn ' + (this.state.note ? 'btn-warning' : 'btn-light')} onClick={() => this.onNoteBtnClick()}><i className="fa fa-sticky-note"></i></button>
      );
    }
  }

  renderNote() {
    if(this.state.noteVisible) {
      return (
        <NoteEditor
          demo={this.props.demo}
          updating={this.state.updating}
          note={this.state.note}
          onSave={(note) => {
            this.setState({ noteVisible: false });
            this.update({ note: note });
          }}
          onCancel={() => this.setState({ noteVisible: false })}
        />
      );
    }
  }

  renderDeleteBtn() {
    if(this.props.deleteable) {
      return (
        <button disabled={this.state.updating} className="btn btn-sm btn-light ml-2" onClick={() => this.deleteSentence()}><i className="fa fa-trash"></i></button>
      );
    }
  }

  renderFavoriteBtn() {
    if(this.props.favoriteable) {
      return (
        <button disabled={this.state.updating} className="btn favorite-btn btn-sm btn-light ml-2" onClick={() => this.toggleFavorite()}>
          <i className={'fa fa-heart' + (this.state.favorited ? '' : '-o')}></i>
        </button>
      );
    }
  }

  renderAudioBtn() {
    return (
      <button className="btn btn-sm btn-light ml-2" onClick={() => this.playAudio()}><i className="fa fa-volume-up"></i></button>
    );
  }

  renderTranslation() {
    if(this.state.editingTranslation) {
      return (
        <div className="mb-2">
          <TranslationEditor
            updating={this.state.updating}
            translation={this.state.translation}
            onSave={(translation) => {
              this.setState({ editingTranslation: false });
              this.update({ translation: translation });
            }}
            onCancel={() => this.setState({ editingTranslation: false })}
          />
        </div>
      );
    }
    const editBtn = <a href="javascript:void(0);" onClick={() => this.setState({ editingTranslation: true })} className="edit-translation"><i className="fa fa-pencil"></i></a>;
    return (
      <div style={{ whiteSpace: 'pre-wrap' }}>{this.state.translation} {this.props.editableTranslation && editBtn}</div>
    );
  }

  renderSource() {
    if(!this.props.articleName && !this.props.source) {
      return null;
    }

    return (
      <div className="text-right">
        <small>
          {this.props.articleName && <span className={this.props.gameSettings.darkMode ? 'text-light' : 'text-muted'}>{this.props.articleName}</span>}
          {this.props.source && <a className="ml-1" href={this.props.source} target="_blank">Source</a>}
        </small>
      </div>
    );
  }

  onPhraseSave(phrase) {
    let phrases = this.state.phrases.slice(0);
    let updated = false;
    for(let i = 0, n = phrases.length; i < n; i++) {
      if(phrases[i].id === phrase.id) {
        phrases[i] = phrase;
        updated = true;
        break;
      }
    }
    if(!updated) {
      phrases.push(phrase);
    }
    this.setState({ phrases: phrases });
    this.phraseEditorModal.hide();
  }

  onPhraseDelete(phraseId) {
    this.setState({ phrases: this.state.phrases.filter((p) => p.id !== phraseId) });
    this.phraseEditorModal.hide();
  }

  renderPhraseSlideout() {
    if(this.props.phrasesPath && this.state.highlightedPhrase) {
      return (
        <Modal title="Phrase Deck" ref={(el) => this.phraseEditorModal = el} onHidden={() => this.setState({ highlightedPhrase: null })}>
          <PhraseEditor
            demo={this.props.demo}
            sentenceId={this.props.sentenceId}
            phrasesPath={this.props.phrasesPath}
            links={this.props.phraseEditorLinks}
            text={this.state.highlightedPhrase}
            onSave={(phrase) => this.onPhraseSave(phrase)}
            onDelete={(phraseId) => this.onPhraseDelete(phraseId)}
            includeHint={true}
          />
        </Modal>
      );
    }
  }

  renderPhraseBtn() {
    if(this.state.phrases.length) {
      // show list of phrases associated with sentence, edit / delete
      return (
        <button disabled={this.state.updating} className="btn phrases-btn btn-sm btn-warning ml-2" data-html="true" data-title="Phrases" data-content={"<ul className=\"mb-0\">" + this.state.phrases.map((p) => "<li><a href=\"#\" class=\"phrase\">" + p.text + "</a></li>").join('') + "</ul>"} data-toggle="popover" data-placement="bottom" data-container={'#' + this.id}>
          <i className="fa fa-book"></i>
        </button>
      );
    }
  }

  renderShareBtn() {
    // social
    // embed
    // forum
    //
    // return (
    //   <button disabled={this.state.updating} className="btn phrases-btn btn-sm btn-light ml-2" onClick={() => {}}>
    //     <i className="fa fa-share-alt"></i>
    //   </button>
    // );
  }

  onMouseUp() {
    if(this.state.playing) {
      return false;
    }

    const selection = getSelectionText();
    if(this.props.text.match(selection)) {
      this.setState({ highlightedPhrase: selection });
    }
  }

  renderSubmitBtn() {
    // Only while actually playing. Before Play the input is disabled so there's
    // nothing to submit, and a second btn-primary sitting next to Play would
    // compete with it for attention when Play is the only thing to do. Same
    // applies while paused.
    if(!this.state.playing || this.state.complete) {
      return null;
    }
    return (
      <button
        // mr-2 rather than the row's usual ml-2-on-the-following-button: this
        // one leads the row and is conditionally rendered, so the gap has to
        // belong to it — otherwise Reset sits indented once the sentence is
        // complete and Submit is gone.
        className="btn btn-sm btn-primary mr-2"
        disabled={!this.state.input.trim()}
        onClick={() => this.submit()}
        title="Submit your answer (Enter)"
      >
        Submit
      </button>
    );
  }

  renderComparison() {
    const { submittedInput } = this.state;
    if(submittedInput === null || this.state.complete) {
      return null;
    }

    // Whitespace-delimited words for most languages, characters for the ones
    // that don't use spaces — otherwise the whole sentence comes back as a
    // single wrong token, which is the problem this is here to solve.
    const spaceless = Sentence.SPACELESS_LANGUAGE_CODES.includes(this.props.twoLetterLanguageCode);
    const { attempt, expected } = compare(this.props.text, submittedInput, { spaceless, caseSensitive: this.caseSensitive });
    const joiner = spaceless ? '' : ' ';

    // del and ins rather than colour alone: strikethrough and underline carry
    // the same information for anyone who can't distinguish red from green.
    const renderLine = (tokens, Tag, className) => (
      <span style={this.isRtlLang ? { direction: 'rtl' } : null}>
        {tokens.map((t, i) => (
          <React.Fragment key={i}>
            {i > 0 ? joiner : ''}
            {t.wrong ? <Tag className={className}>{t.token}</Tag> : t.token}
          </React.Fragment>
        ))}
      </span>
    );

    return (
      <div className="card mt-2">
        <div className="card-body py-2">
          <div className="mb-2">
            <small className="text-muted d-block">You wrote</small>
            {renderLine(attempt, 'del', 'text-danger')}
          </div>
          <div>
            <small className="text-muted d-block">Expected</small>
            {renderLine(expected, 'ins', 'text-success')}
          </div>
          <small className="text-muted d-block mt-2">
            Edit your answer above and submit again, or carry on.
          </small>
          {this.props.aiEnabled && (
            <button
              className="btn btn-sm btn-outline-primary mt-2"
              disabled={!!this.state.aiLoading}
              onClick={() => this.askAi('grade')}
            >
              {this.aiShowing('grade') ? 'Hide' : 'Ask AI — is mine OK?'}
            </button>
          )}
          {this.renderAiResult()}
        </div>
      </div>
    );
  }

  renderGameControls() {
    return (
      <>
        <Textarea
          disabled={!this.state.playing || this.state.complete}
          className={[
            'game-input',
            'form-control',
            (!this.state.complete && this.props.gameSettings.darkMode && 'bg-dark'),
            (!this.state.complete && this.props.gameSettings.darkMode && !this.state.input && 'text-white'),
            // Green while you're on track, neutral otherwise — no red. Turning
            // the whole input red on one wrong character is the live form of the
            // most-reported complaint in six years of feedback, and the live hint
            // no longer needs to carry that load: submit-and-compare reports the
            // actual errors. Losing the green is signal enough.
            //
            // Only the display is gated on the setting — correctSoFar keeps
            // being computed either way, because the typo counter derives from
            // its transitions and feeds SRS grading.
            (!this.state.complete && this.colorHints && this.state.correctSoFar && 'text-success'),
            (this.state.complete && 'text-white bg-success'),
          ].filter((c) => c).join(' ')}
          value={this.state.input}
          onChange={this.onInputChange.bind(this)}
          ref={(el) => this.inputRef = el}
          onKeyDown={this.onInputKeyDown.bind(this)}
          onKeyUp={this.onInputKeyUp.bind(this)}
          style={this.isRtlLang ? { direction: 'rtl' } : null}
        ></Textarea>
        <div className="row" style={{ marginTop: 4 }}>
          <div className="col-9" style={{ marginTop: 4 }}>
            {/* Always visible: the controls you use mid-sentence. */}
            {this.renderSubmitBtn()}
            {this.renderPlayPauseResetBtn()}
            {this.renderHintBtn()}
            {this.renderWordHintBtn()}
            {this.renderAudioBtn()}

            {/* The rest inline on sm and up, behind a More menu below that. The
                row carries ten controls and several now have text labels, which
                wraps badly on a phone. Rendered twice rather than moved, so the
                desktop layout is untouched. */}
            <span className="d-none d-sm-inline">
              {this.renderSecondaryBtns()}
            </span>
            <span className="dropdown d-inline-block d-sm-none">
              <button
                className="btn btn-sm btn-light dropdown-toggle ml-2"
                type="button"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                More
              </button>
              <div className="dropdown-menu p-2 sentence-more-menu">
                {this.renderSecondaryBtns()}
              </div>
            </span>
          </div>
          <div className="col-3 text-right" style={{ marginTop: 4 }}>
            {this.renderDeleteBtn()}
          </div>
        </div>
        {this.renderAccentButtons()}
        {this.renderComparison()}
        {/* The comparison panel renders its own copy for Ask AI. This one covers
            Explain, which is available with or without a submitted answer. */}
        {this.state.submittedInput === null && this.renderAiResult()}
        {this.renderReviewQueueSuggestion()}
        {this.renderNote()}
        <div className="text-success logo" ref={this.wordCountRef} style={{ display: this.state.wordCountVisible ? 'block' : 'none', zIndex: 999, textShadow: '6px 6px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', position: 'fixed', top: '20%', left: 0, textAlign: 'center', width: '100%', fontWeight: 'bold', fontSize: '10em' }}>
          +{this.props.wordCount}
        </div>
      </>
    );
  }

  onTranslationAccepted() {
    if(this.props.sentenceId && !this.props.demo) {
      // not using update - don't want to wait for respone or update state from attributes
      $.ajax({
        method: 'put',
        url: '/sentences/' + this.props.sentenceId,
        data: { sentence: { translation_accepted: true } }
      });
    }
    this.setState({ translationAccepted: true }, () => this.onPlayPauseClick());
  }

  renderPreTranslationAcceptedControls() {
    return (
      <div className="mt-2">
        <div className="row">
          <div className="col-9">
            {/* Just "Play". It matches the Play/Pause button that replaces it
                once the sentence starts, so the same word means the same thing
                throughout. "Edit Translation" is gone: renderTranslation already
                puts a pencil next to the translation itself, which is both
                closer to the thing it edits and correctly hidden when
                editableTranslation is false — which the text button wasn't. */}
            <button ref={(el) => this.acceptBtnRef = el} className="btn btn-primary btn-sm" onClick={() => this.onTranslationAccepted()}>Play</button>
            {this.renderAudioBtn()}
            {this.renderFavoriteBtn()}
            {this.renderNoteBtn()}
            {this.renderPhraseBtn()}
            {this.renderShareBtn()}
          </div>
          <div className="col-3 text-right" style={{ marginTop: 4 }}>
            {this.renderDeleteBtn()}
          </div>
        </div>
        {this.renderNote()}
      </div>
    );
  }

  renderOverLimitModal() {
    if(this.state.showOverLimitModal) {
      return (
        <Modal noClose={true} size="large" title="Daily Word Limit">
          <div className="text-center">
            <h4 className="mb-4">Oh no! You've hit your daily limit for the free plan!</h4>
            <h4 className="mb-4">Subscribe for unlimited access to <span className="logo">The Great Translation Game</span>.</h4>
          </div>
          <a href="/subscribe" className="btn btn-block btn-primary btn-lg">Subscribe!</a>
          <div className="mt-1 text-center"><small>or go back to your <a href="/">Dashboard</a></small></div>
        </Modal>
      );
    }
  }

  getTimeStr(t) {
    const seconds = t % 60;
    return Math.floor(t / 60) + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  renderCompletions() {
    return (
      <Completions
        completions={this.state.completions}
        getTimeStr={(t) => this.getTimeStr(t)}
      />
    );
  }

  renderStats() {
    const { complete, elapsedTime, hintCount, pauseCount, translationAccepted, typoCount } = this.state;

    if(!translationAccepted) {
      return null;
    }

    if(complete) {
      return this.renderCompletions();
    }

    return (
      <div><small>Typos: {typoCount} Hints: {hintCount} Pauses: {pauseCount} Time: {this.getTimeStr(elapsedTime)}</small></div>
    );
  }

  render() {
    return (
      <div id={this.id} className={'sentence mb-3' + (this.state.complete ? ' complete' : '') + (this.props.isNext && !this.state.complete ? ' next-up' : '')} ref={(el) => this.ref = el}>
        {this.renderFirstSentenceHelp()}
        <div className={'card' + (this.props.gameSettings.darkMode ? ' bg-dark text-white' : '')}>
          <div className="card-body">
            {/* Hidden by opacity while playing, not by painting the text the
                same colour as the card. Colour-matching breaks the moment
                anything changes the background underneath it — which is exactly
                what the next-up highlight does. */}
            <div className={'text' + (this.state.playing ? ' hidden-while-playing disable-selection' : '')} style={{ fontSize: '1.25em', direction: this.isRtlLang ? 'rtl' : null }} onMouseUp={() => this.onMouseUp()}><strong>{this.props.text}</strong></div>
            {this.renderTranslation()}
            {this.state.translationAccepted ? this.renderGameControls() : this.renderPreTranslationAcceptedControls()}
            {this.renderStats()}
          </div>
        </div>
        {this.renderSource()}
        {this.renderPhraseSlideout()}
        {this.renderOverLimitModal()}
      </div>
    );
  }
}
